import React, { Fragment } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { GenericTable } from '../../components/_EOS/Table'
import { WebhookSubscriber } from '../../interfaces/Webhook'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { ApiConfig } from '../../config/api'
import axios from 'axios'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { usePagination } from '../../hooks/usePagination'
import { WebhookForm, WebhookFormEdit } from './form'
import { Status } from '../../hooks/useFetchState'
import { WebhookSearch } from './search/WebhookSearch'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'

interface WebhookPageProps {}

export interface WebhookSearchState {
  selected: string
  value: string
}

const getWebhooks = (
  searchState: WebhookSearchState = { selected: '', value: '' },
  page = 0,
  pageSize = 10
) => {
  const url = new URL(`${ApiConfig.WebhookApi}`)

  url.searchParams.set('page', page.toString())

  url.searchParams.set('pageSize', pageSize.toString())

  if (searchState.selected && searchState.value) {
    url.searchParams.set(searchState.selected, searchState.selected)
  }

  if (searchState.selected && searchState.value) {
    url.searchParams.set(searchState.selected, searchState.value)
  }

  return axios.get(url.toString())
}

const deleteWebhook = (webhookSubscriberId: number) => {
  const url = new URL(`${ApiConfig.WebhookApi}/${webhookSubscriberId}`)

  return axios.delete(url.toString())
}

export const WebhookPage: React.FC<WebhookPageProps> = () => {
  const [searchState, setSearchState] = React.useState({
    selected: '0',
    value: ''
  })
  const { state, callApi } = useApi<ApiResponse<WebhookSubscriber>>()
  const { state: deleteState, callApi: deleteCallApi } = useApi()

  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const editModal = useModal<WebhookSubscriber>('Editar webhook')
  const createModal = useModal('Crear webhook')

  const pagination = usePagination(5)

  const requestWebhooks = (
    searchState?: WebhookSearchState,
    page?: number,
    pageSize?: number
  ) => {
    callApi(getWebhooks(searchState, page, pageSize)).catch((err) =>
      console.log(err)
    )
  }

  const requestDeleteWebhook = (webhookSubscriberId: number) => {
    deleteCallApi(deleteWebhook(webhookSubscriberId))
      .then(() => requestWebhooks())
      .catch((err) => console.log(err))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // detectar si cambio la cantidad de filas por página
    // si lo hizo, vamos a la página 0
    if (pageSize === pagination.pageSize) {
      requestWebhooks(searchState, page, pageSize)
      pagination.setPagination(page, pageSize)
    } else {
      requestWebhooks(searchState, 0, pageSize)
      pagination.setPagination(0, pageSize)
    }
  }

  const handleCreateWebhook = (success: boolean) => {
    if (success) {
      createModal.closeModal()
      requestWebhooks(undefined, pagination.page, pagination.pageSize)
    }
  }

  function getHostName(url: string): string {
    try {
      const parsedUrl = new URL(url)
      return parsedUrl.hostname
    } catch (error) {
      return url
    }
  }

  const handleCloseEditModal = (success: boolean) => {
    if (success) {
      editModal.closeModal()
      requestWebhooks(searchState, pagination.page, pagination.pageSize)
    }
  }

  return (
    <Fragment>
      <StandardPage
        pageTitle='Administrador de Webhooks'
        buttonAction={() => createModal.openModal('md')}
        buttonTitle='Crear Webhook'
      >
        <WebhookSearch
          searchState={searchState}
          setSearchState={setSearchState}
          pagination={pagination}
          onSearchClick={requestWebhooks}
        />
        <GenericTable<WebhookSubscriber>
          headersConfig={[
            { field: 'webhookSubscriberId', title: 'ID' },
            {
              field: 'webHookUrl',
              title: 'URL',
              formater: (url) => getHostName(url)
            }
          ]}
          IDKey={'webhookSubscriberId'}
          data={state.data?.items}
          actions={{
            onEdit: (_id, row) => editModal.openModal('md', row),
            onDelete: (id) => requestDeleteWebhook(id)
          }}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={
            state.status === Status.Loading ||
            deleteState.status === Status.Loading
          }
        />
      </StandardPage>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        onClose={editModal.closeModal}
        title={editModal.title}
      >
        {editModal.extendedProps && (
          <WebhookFormEdit
            webhookSubscriber={editModal.extendedProps}
            closeModal={handleCloseEditModal}
            handleSnackbar={openSnackBar}
          />
        )}
      </Modal>
      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        onClose={createModal.closeModal}
        title={createModal.title}
      >
        <WebhookForm
          onCreate={handleCreateWebhook}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
